import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { Navigate, useParams } from 'react-router';

import { useGetProjectQuery } from 'features/projects/api/projects';
import { isSubProjectDetail } from 'features/projects/ui/project-detail.types';

import useMortgageRules from '../hooks/useMortgageRules';
import MortgageFinancialEntityCard from './MortgageFinancialEntityCard';
import MortgageFinancialEntityView from './MortgageFinancialEntityView';
import MortgageSubProjectsMatrix from './MortgageSubProjectsMatrix';

/*
 * Two kinds of company, told apart by the master's own mark (`isFinancialEntity`), not by its sub-projects.
 * A financial entity (Lead Management) answers with a single prompt: its master and each of its products get
 * the same two cards. A real estate (Brokerage) switches its financing agents on per sub-project and moves
 * clients between them, so the screen is a matrix over its children.
 */
const MortgageMasterView = (): ReactElement => {
  const { masterProjectId } = useParams<{ masterProjectId: string }>();
  const projectId = Number(masterProjectId);

  const { data: project, isLoading, isError } = useGetProjectQuery(projectId, { skip: !masterProjectId });

  const isFinancialEntity = project?.isFinancialEntity ?? false;

  /* An entity reads its rules on the master until a client gets a product; a real estate only on its children. */
  const entityIds = useMemo(() => {
    if (!project) return [];
    const subProjectIds = project.subProjects.map((subProject) => subProject.id);
    return isFinancialEntity ? [project.id, ...subProjectIds] : subProjectIds;
  }, [project, isFinancialEntity]);

  const { byProject, isLoading: areRulesLoading } = useMortgageRules(entityIds);

  if (isLoading || areRulesLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div className="h-16 bg-bg-surface-raised rounded-xl animate-pulse" />
        <div className="h-96 bg-bg-surface-raised rounded-xl animate-pulse" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
        No se pudo cargar la configuración hipotecaria de la inmobiliaria.
      </div>
    );
  }

  /* The screen is the master's: the rules are configured for the whole portfolio at once. */
  if (isSubProjectDetail(project)) {
    return <Navigate to={`/project/${project.masterProjectId}/mortgage`} replace />;
  }

  /*
   * Keyed on the project: this is the element behind every /project/:id/mortgage, so React would reuse
   * it across companies and the table's selection would survive the change. The skeleton does not
   * cover it either — `isLoading` stays false when the destination is already in the RTK cache — and a
   * selection carried over writes rules into the sub-projects of the company the operator just left.
   */
  return (
    <div key={project.id} className="flex flex-col gap-5">
      <header>
        <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">
          {isFinancialEntity ? 'Entidad financiera' : 'Inmobiliaria'}
        </p>
        <h2 className="text-2xl font-bold text-text-default m-0 font-display">{project.name}</h2>
        <span className="text-sm text-text-muted font-body">
          ID: {project.id} · {project.countryCode}
        </span>
      </header>

      <MortgageFinancialEntityCard
        projectId={project.id}
        projectName={project.name}
        isFinancialEntity={isFinancialEntity}
      />

      {isFinancialEntity ? (
        <MortgageFinancialEntityView project={project} subProjects={project.subProjects} byProject={byProject} />
      ) : project.subProjects.length > 0 ? (
        <MortgageSubProjectsMatrix
          subProjects={project.subProjects}
          byProject={byProject}
          countryCode={project.countryCode}
        />
      ) : (
        <p className="rounded-md border border-border p-6 text-sm text-text-muted">
          Esta inmobiliaria no tiene subproyectos. Los agentes financieros se activan por subproyecto; si es una entidad
          financiera, márcala arriba.
        </p>
      )}
    </div>
  );
};

export default MortgageMasterView;
